import React, { FC, useState } from 'react';

import PageHeader from '../common/layout/PageHeader';
import { CREATED_AT, DELETED, EMAIL, FIRST_NAME, ID, SEARCH_BY_EMAIL } from '../../utils/constants/labels';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlayCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { debounce } from '../../utils/common';
import { Input, Tooltip } from 'antd';
import CreationModal from '../common/modal/CreationModal';
import Table from '../common/table/Table';
import { api } from '../../api/api';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import colors from '../../utils/colors';
import * as routes from '../../utils/constants/routes';
import { ImportUsersInOrganizationForm } from './ImportUsersInOrganizationForm';
import { copyToClipboard, DatePreview, initStudentSession, NotificationType, notify } from '../../utils/helpers';
import { handleErrorResponse } from '../../api/network';
import SafeAction from '../common/SafeAction';
import { DATE_FORMAT, formatDate } from '../../utils/timeUtils';
import UpdateUserEmail from '../common/modal/UpdateUserEmailModal';
import { User } from '../../types/userTypes';

const columns = (openUpdateUserEmailModal: (user: User) => void, refreshTable: () => void) => [
  {
    title: ID,
    dataIndex: 'id',
    sorter: true,
    width: '5%',
    defaultSortOrder: 'descend'
  },
  {
    title: FIRST_NAME,
    dataIndex: 'firstName',
    width: '25%'
  },
  {
    title: EMAIL,
    dataIndex: 'email',
    sorter: true,
    width: '30%'
  },
  {
    title: CREATED_AT,
    dataIndex: 'createdAt',
    sorter: true,
    width: '15%',
    render: value => (value ? <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview> : null)
  },
  {
    title: DELETED,
    dataIndex: 'deleted',
    width: '5%',
    render: value => (value ? <CheckOutlined /> : <CloseOutlined />)
  },
  {
    title: '',
    dataIndex: 'resendInvitation',
    width: '10%',
    render: (value, row) => {
      if (row.redeemAccessCode) {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Unredeemed</span>
            {/*
             * we didn't call resendUserInvite since we assume that users will not be invited to platform as they used to be earlier,
             * but they will sign up by themselves
             */}
            <SafeAction
              title={'Resend'}
              tooltip={'Resend user activation'}
              action={() => api.resendUserActivation(row.id)}
              doneText={'Sent'}
            />
            <Tooltip title="Copy activation link to clipboard">
              <CopyOutlined
                onClick={() => copyToClipboard(`https://app.shovelapp.io/-/set-password/${row.redeemAccessCode}`)}
              />
            </Tooltip>
          </div>
        );
      }
      if (row.emailVerificationCode) {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Unverified</span>
            <SafeAction
              title={'Resend'}
              tooltip={'Resend email verification'}
              action={() => api.resendUserEmailVerification(row.id)}
              doneText={'Sent'}
            />
            <Tooltip title="Copy email verification link to clipboard">
              <CopyOutlined
                onClick={() =>
                  copyToClipboard(`https://app.shovelapp.io?email-verification-code=${row.emailVerificationCode}`)
                }
              />
            </Tooltip>
          </div>
        );
      }
      return null;
    }
  },
  {
    title: '',
    dataIndex: 'generateResetPasswordCode',
    width: '5%',
    render: (value, row) =>
      !row.deleted &&
      !row.redeemAccessCode && (
        <SafeAction
          Icon={<KeyOutlined />}
          tooltip={'Generate reset password code'}
          action={async () => {
            const res = await api.generateUserResetPasswordCode(row.id);
            if (res.ok) {
              copyToClipboard(res.data);
            }
            return res;
          }}
          onError={(message: string) => notify(message)}
          doneText={'Reset password code generated'}
        />
      )
  },
  {
    title: '',
    dataIndex: 'editUserEmail',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'Update Email'}>
          <EditOutlined onClick={() => openUpdateUserEmailModal(row)} />
        </Tooltip>
      )
  },
  {
    title: '',
    dataIndex: 'editUserEmail',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <Tooltip title={'Start Session'}>
          <PlayCircleOutlined onClick={() => initStudentSession(row.id)} />
        </Tooltip>
      )
  },
  {
    title: '',
    dataIndex: 'deleteAccount',
    width: '5%',
    render: (value, row) =>
      !row.deleted && (
        <SafeAction
          Icon={<DeleteOutlined style={{ color: colors.negative }} />}
          action={async () => {
            const res = await api.deleteAccount(row?.id!);
            if (res.ok) {
              refreshTable();
            }
            return res;
          }}
          onError={(message: string) => notify(message)}
          doneText={'User is deleted'}
        />
      )
  }
];

type Props = {
  location: any;
};

const Organization: FC<Props> = ({ location }) => {
  const [email, setEmail] = useState('');
  const [refreshTable, setRefreshTable] = useState(null);
  const history = useHistory();

  const [editUser, setEditUser] = useState<User>();

  if (!location.state) {
    history.push(`${routes.DASHBOARD}${routes.ORGANIZATIONS}`);
    return null;
  }

  const { organization } = location.state;

  const setEmailDebounced = debounce(value => setEmail(value), 400);

  return (
    <Container>
      <PageHeader
        onBack={() => history.goBack()}
        title={organization.name}
        extra={[
          <Input
            key={1}
            onChange={e => setEmailDebounced(e.target.value)}
            placeholder={SEARCH_BY_EMAIL}
            prefix={<SearchOutlined />}
          />,
          <CreationModal
            key={2}
            title={'Import users'}
            onCreate={async file => {
              const response = await api.importUsersInOrganization(organization.id, file);
              if (!response.ok) {
                handleErrorResponse(response, history);
                return false;
              }
              notify(response.data ? response.data.message : 'Users successfully imported', NotificationType.SUCCESS);
              // @ts-ignore
              await refreshTable();
              return true;
            }}
            ModalForm={ImportUsersInOrganizationForm}
          />
        ]}
      />
      <Table
        columns={columns((user: User) => setEditUser(user), refreshTable!)}
        getData={params => api.getUsersWithinOrganization(organization.id, params)}
        idPropName={'id'}
        searchPropName={'email'}
        searchPropValue={email}
        bindFetchFn={setRefreshTable}
      />
      <UpdateUserEmail user={editUser} onClose={() => setEditUser(undefined)} onUserUpdate={refreshTable!} />
    </Container>
  );
};

export default Organization;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;
